import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import Footer from "./footer";
import { useAuth } from "../hooks/use-auth";
import FreeDemoRequestPopup from "./FreeDemoRequestPopup";

const NAVBAR_HEIGHT = 56; // Fixed navbar height in pixels
const SIDEBAR_WIDTH_OPEN = 240; // Must match Sidebar w-[240px]
const SIDEBAR_WIDTH_COLLAPSED = 64; // Must match Sidebar w-16 (4rem = 64px)

const STAFF_ROLES = [
  "owner",
  "manager",
  "staff",
  "chef",
  "businessowner",
  "captain",
  "waiter",
  "kitchen",
  "cashier",
  "bartender",
  "host",
  "inventory manager",
  "accountant",
  "delivery",
  "marketing manager",
  "supervisor",
  "generalmanager",
  "admin",
  "superadmin",
  "systemadmin",
  "businessmanager"
];

const Layout: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, role, planStatus } = useAuth();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    window.innerWidth >= 1024,
  );
  const [showDemoPopup, setShowDemoPopup] = useState(false);

  // Popup Logic - Show only ONCE per session and respect user preference
  useEffect(() => {
    // Check if user dismissed popup or already submitted
    const demoPopupDismissed = sessionStorage.getItem('demoPopupDismissed');
    const demoRequestSubmitted = localStorage.getItem('demoRequestSubmitted');
    
    // If already dismissed this session or submitted before, don't show
    if (demoPopupDismissed || demoRequestSubmitted) {
      return;
    }

    // Check if user is NOT authenticated (Prospective Customer)
    // AND not on specific pages (Bill, Login, Register, Captain, Customer)
    const isExcludedPage =
      location.pathname.startsWith("/bill") ||
      location.pathname.startsWith("/captain") ||
      location.pathname.startsWith("/customer") ||
      location.pathname.startsWith("/qr") ||
      location.pathname.startsWith("/login") ||
      location.pathname.startsWith("/register");

    // Only auto-show popup on home page (not plan-section - users there are already interested in pricing)
    const isHomePage = location.pathname === "/";

    if (!isAuthenticated && !isExcludedPage && isHomePage) {
      const timer = setTimeout(() => {
        setShowDemoPopup(true);
      }, 1500); // 1.5 seconds delay (less annoying)
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, location.pathname]);

  const handleClosePopup = () => {
    setShowDemoPopup(false);
    // Remember that user dismissed popup for this session
    sessionStorage.setItem('demoPopupDismissed', 'true');
  };

  const handlePopupSuccess = () => {
    setShowDemoPopup(false);
    // Remember permanently that user submitted demo request
    localStorage.setItem('demoRequestSubmitted', 'true');
  };

  // Expose function to open popup from other components (e.g., Navbar button)
  useEffect(() => {
    (window as any).openDemoPopup = () => {
      setShowDemoPopup(true);
    };
    return () => {
      delete (window as any).openDemoPopup;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const newIsDesktop = window.innerWidth >= 1024;
      setIsDesktop(newIsDesktop);
      if (!newIsDesktop) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (
      isDesktop &&
      isAuthenticated &&
      STAFF_ROLES.includes(role?.toLowerCase() || "") &&
      planStatus === "active"
    ) {
      setIsSidebarOpen(true);
    }
  }, [isDesktop, isAuthenticated, role, planStatus]);

  const hideNavbarRoutes = ["/register", "/login"];
  const shouldHideNavbar = hideNavbarRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  const normalizedRole = role ? role.toLowerCase() : "";
  const isMainPage = location.pathname === "/";
  
  // LOGIC FOR HIDING FOOTER:
  // 1. If user is authenticated and is a Staff/Owner (using the app), HIDE FOOTER.
  // 2. If it's a specific "No Footer Route" (like POS, KDS), HIDE FOOTER.
  // 3. Keep existing logic for customers/public pages.
  
  const isStaffOrOwner = isAuthenticated && STAFF_ROLES.includes(normalizedRole);
  
  const noFooterRoutes = ["/pos", "/dashboard", "/kitchen-display", "/tables", "/order-management", "/bill", "/captain", "/inventory"];
  const isNoFooterRoute = noFooterRoutes.some(route => location.pathname.startsWith(route));

  const shouldHideFooter = isStaffOrOwner || isNoFooterRoute || 
    (!isMainPage && (!isAuthenticated || normalizedRole === "customer"));

  const isCustomerPage =
    location.pathname.startsWith("/track-order") ||
    (location.pathname.startsWith("/restaurant") && !isAuthenticated) ||
    location.pathname.startsWith("/r/");

  const shouldShowSidebar =
    isAuthenticated &&
    STAFF_ROLES.includes(normalizedRole) &&
    planStatus === "active" &&
    !isCustomerPage;

  const isPublicPage = [
    "/",
    "/about",
    "/plan-section",
    "/how-it-works",
    "/faq",
    "/demo",
    "/benefits",
  ].includes(location.pathname);

  // Calculate content margin based on sidebar state
  const getContentStyle = (): React.CSSProperties => {
    if (!shouldShowSidebar || !isDesktop) {
      return { marginLeft: 0 };
    }
    return {
      marginLeft: isSidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_COLLAPSED,
    };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Navbar - z-50 to stay above content */}
      {!shouldHideNavbar && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <Navbar
            onSidebarToggle={(isOpen) => setIsSidebarOpen(isOpen)}
            sidebarOpen={isSidebarOpen}
            showSidebar={shouldShowSidebar && isDesktop}
          />
        </div>
      )}

      {/* Sidebar */}
      {shouldShowSidebar && (
        <Sidebar isOpen={isSidebarOpen} onToggle={setIsSidebarOpen} />
      )}

      {/* Main Content Area */}
      <div
        className="transition-all duration-300 ease-in-out overflow-y-auto"
        style={{
          ...getContentStyle(),
          minHeight: "100vh",
          maxHeight:
            "calc(100vh - " +
            (shouldHideNavbar || isPublicPage ? 0 : NAVBAR_HEIGHT) +
            "px)",
          paddingTop: shouldHideNavbar || isPublicPage ? 0 : NAVBAR_HEIGHT,
        }}
      >
        <main className="flex-grow">
          <Outlet />
        </main>

        {!shouldHideFooter && <Footer />}
      </div>

      {/* Free Demo Request Popup */}
      <FreeDemoRequestPopup
        isOpen={showDemoPopup}
        onClose={handleClosePopup}
        onSuccess={handlePopupSuccess}
      />
    </div>
  );
};

export default Layout;
